import { closeModal, openModal } from '../navigationController';
import type { BillingUpgradePreviewResponse } from '../apiClientResponses';
import type { UpgradeAttemptResult } from '../commerceController';
import type { Root } from '../prototypeTypes';
import { ensureBusinessOverlayMounted } from '../businessOverlayLifecycle';

type UpgradeConfirmHandlers = {
	onConfirm: (canWriteUi: () => boolean) => UpgradeAttemptResult | Promise<UpgradeAttemptResult>;
};

// The last presented confirmation survives only until it is acted on or the
// root is torn down; the modal is informational (no form input), so an overlay
// click may dismiss it without clearing this in a way that matters.
const pendingConfirmation = new WeakMap<Root, UpgradeConfirmHandlers>();

// Generic copy — no entitlement amounts or dates, the server preview already
// validated the charge path and chose the behavior.
const CONFIRM_COPY = {
	same_cycle_proration:
		'Your billing cycle and renewal date stay the same. You\u2019ll pay only the prorated difference today, and your new plan\u2019s quota unlocks right away.',
	cross_cycle_refund_and_rebill:
		'This upgrade starts a brand-new billing period. The remaining value of your current period will be refunded to your original payment method, and your new plan\u2019s entitlements apply from today.',
} as const;

/**
 * Present the paid-upgrade confirmation after the server preview succeeded,
 * so the copy reflects the authoritative billing behavior. Confirming runs the
 * real charge via commerceController.previewUpgrade.
 */
export function presentUpgradeConfirmPrompt(root: Root, preview: BillingUpgradePreviewResponse, onConfirm: UpgradeConfirmHandlers['onConfirm']): void {
	ensureBusinessOverlayMounted(root, 'upgradeConfirmModal');
	root.querySelectorAll<HTMLElement>('#upgradeConfirmTargetPlan').forEach((element) => {
		element.textContent = preview.toPlan.charAt(0).toUpperCase() + preview.toPlan.slice(1);
	});
	const body = root.querySelector<HTMLElement>('#upgradeConfirmBody');
	if (body) body.textContent = CONFIRM_COPY[preview.behavior];
	pendingConfirmation.set(root, { onConfirm });
	// Fold step 2 into the paywall's history entry when the paywall is still on
	// top. The upgrade success path closes the paywall (previewUpgrade) while
	// this modal is active; if the confirm pushed its own entry the paywall
	// entry would be orphaned, and a later unconsumed popstate would re-apply a
	// stale URL (the recurring jump to /membership-billing). Same intent, same
	// URL — one shared entry is correct.
	const paywallOnTop = root.querySelector<HTMLElement>('#paywallModal')?.classList.contains('active') === true;
	openModal(root, 'upgradeConfirmModal', undefined, { pushHistory: !paywallOnTop });
}

export async function confirmUpgradeFromPrompt(root: Root): Promise<void> {
	const handlers = pendingConfirmation.get(root);
	if (!handlers) {
		closeUpgradeConfirmModal(root);
		return;
	}
	const button = root.querySelector<HTMLButtonElement>('#confirmUpgradeBtn');
	if (button?.dataset.submitting === 'true') return;
	if (button) {
		button.dataset.submitting = 'true';
		button.disabled = true;
		button.setAttribute('aria-busy', 'true');
		button.textContent = 'Processing...';
	}
	clearConfirmError(root);
	const ownsPrompt = () => pendingConfirmation.get(root) === handlers
		&& root.querySelector<HTMLElement>('#upgradeConfirmModal')?.classList.contains('active') === true;
	let outcome: UpgradeAttemptResult;
	try {
		outcome = await handlers.onConfirm(ownsPrompt);
	} catch {
		// Defensive: an unexpected throw must never present as a silent success;
		// keep the modal open with the generic error instead of closing it.
		outcome = { ok: false, message: UPGRADE_FAILED_COPY };
	} finally {
		if (button && ownsPrompt()) {
			delete button.dataset.submitting;
			button.disabled = false;
			button.removeAttribute('aria-busy');
			button.textContent = 'Confirm & Pay';
		}
	}
	if (!ownsPrompt()) return;
	if (outcome.ok) {
		closeUpgradeConfirmModal(root);
	} else if ('keepOpen' in outcome) {
		// A secondary prompt (resume auto-renew) is now on top; keep step 2 open
		// underneath so the user can re-confirm once auto-renew is restored.
	} else {
		setConfirmError(root, outcome.message);
	}
}

const UPGRADE_FAILED_COPY = 'Your upgrade could not be completed. No charge was made. Please try again.';

function setConfirmError(root: Root, message: string): void {
	const element = root.querySelector<HTMLElement>('#upgradeConfirmError');
	if (!element) return;
	element.textContent = message;
	element.hidden = false;
}

function clearConfirmError(root: Root): void {
	const element = root.querySelector<HTMLElement>('#upgradeConfirmError');
	if (!element) return;
	element.textContent = '';
	element.hidden = true;
}

export function closeUpgradeConfirmModal(root: Root): void {
	closeModal(root, 'upgradeConfirmModal');
	const overlay = root.querySelector<HTMLElement>('#upgradeConfirmModal');
	if (overlay) overlay.hidden = true;
	clearUpgradeConfirmPrompt(root);
}

/** Clear a pending charge handler when another navigation owns visual teardown. */
export function clearUpgradeConfirmPrompt(root: Root): void {
	pendingConfirmation.delete(root);
}
